import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import CustomTabs from 'components/CustomTabs';
import { authContext } from 'contexts/AuthContext';
import {
  collectPermissionNames,
  hasAnyPermission,
  syncUserPermissions,
} from 'utils/permissions';
import { FiLock } from 'react-icons/fi';
import SearchRateTab from './SearchRateTab';
import MatrixTab from './MatrixTab';
import RequestRateTab from './RequestRateTab';
import ListTab from './ListTab';
import './DeliveredRate.css';

const TAB_PERMISSION_MAP = {
  'Search Rate': ['SEARCH_DELIVERY_RATE', 'DELIVERY_RATE_SEARCH'],
  Matrix: ['MATRIX_DELIVERY_RATE', 'DELIVERY_RATE_MATRIX'],
  'Request Rate': ['REQUEST_DELIVERY_RATE', 'DELIVERY_RATE_REQUEST'],
  List: ['LIST_DELIVERY_RATE', 'DELIVERY_RATE_LIST'],
};

const DeliveredRate = () => {
  const { auth, setAuthData } = useContext(authContext);
  const tabs = ['Search Rate', 'Matrix', 'Request Rate', 'List'];
  const requestTabs = ['Staff Request', 'Customer Request'];
  const [searchParams, setSearchParams] = useSearchParams();
  const toTabParam = (tab) => tab.toLowerCase().replace(/\s+/g, '-');
  const requestedTab = searchParams.get('tab');
  const requestedRequestTab = searchParams.get('requestTab');
  const [selectedTab, setSelectedTab] = useState(
    () => tabs.find((tab) => toTabParam(tab) === requestedTab) || 'Search Rate',
  );
  const [selectedRequestTab, setSelectedRequestTab] = useState(
    () => requestTabs.find((tab) => toTabParam(tab) === requestedRequestTab) || 'Staff Request',
  );
  const [requestStatusFilter, setRequestStatusFilter] = useState('pending');
  const [headerActions, setHeaderActions] = useState(null);
  const syncTabsToUrl = searchParams.has('tab') || searchParams.has('requestTab');

  useEffect(() => {
    if (auth?.data?.token) {
      syncUserPermissions(auth.data, setAuthData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const permissionNames = useMemo(
    () => collectPermissionNames(auth?.data),
    [auth?.data],
  );

  const hasTabPermission = useCallback(
    (tabName) => {
      const candidates = TAB_PERMISSION_MAP[tabName];
      if (!candidates) return true;
      return hasAnyPermission(permissionNames, candidates);
    },
    [permissionNames],
  );

  const updateTabSearchParams = (tab, requestTab) => {
    if (!syncTabsToUrl) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('tab', toTabParam(tab));
    if (tab === 'Request Rate') {
      nextSearchParams.set('requestTab', toTabParam(requestTab));
    } else {
      nextSearchParams.delete('requestTab');
    }
    setSearchParams(nextSearchParams, { replace: true });
  };

  const handleTabChange = (newTab, requestTab = selectedRequestTab) => {
    setHeaderActions(null);
    setSelectedTab(newTab);
    updateTabSearchParams(newTab, requestTab);
  };

  const handleRequestTabChange = (newTab) => {
    setHeaderActions(null);
    setSelectedRequestTab(newTab);
    updateTabSearchParams(selectedTab, newTab);
  };

  const handleAddRequest = () => {
    setSelectedRequestTab('Staff Request');
    handleTabChange('Request Rate', 'Staff Request');
  };

  const canAccessSelectedTab = hasTabPermission(selectedTab);

  return (
    <div className="delivered-rate-page">
      {/* Header with Tabs */}
      <div
        className={`delivered-rate-header${selectedTab === 'Request Rate' ? ' delivered-rate-header--request' : ''}${selectedTab === 'List' ? ' delivered-rate-header--list' : ''}`}
      >
        <div className="delivered-rate-header__content">
          <div className="delivered-rate-tabs-wrapper">
            <CustomTabs
              tabs={tabs}
              selectedTab={selectedTab}
              onTabChange={handleTabChange}
              className="mb-0"
            />
          </div>
          {canAccessSelectedTab && headerActions && (
            <div className="delivered-rate-header-actions">{headerActions}</div>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="delivered-rate-container">
        {canAccessSelectedTab ? (
          <>
            {selectedTab === 'Search Rate' && (
              <SearchRateTab onAddRequest={handleAddRequest} />
            )}
            {selectedTab === 'Matrix' && (
              <MatrixTab setHeaderActions={setHeaderActions} />
            )}
            {selectedTab === 'Request Rate' && (
              <div className="delivered-rate-request-section">
                <div className="delivered-rate-request-tabs-wrapper">
                  <CustomTabs
                    tabs={requestTabs}
                    selectedTab={selectedRequestTab}
                    onTabChange={handleRequestTabChange}
                    className="mb-0 delivered-rate-request-tabs"
                  />
                  <div className="delivered-rate-request-inline-status">
                    <span>Status</span>
                    <select
                      aria-label="Filter requests by status"
                      value={requestStatusFilter}
                      onChange={(event) => setRequestStatusFilter(event.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="">All</option>
                    </select>
                  </div>
                </div>
                <RequestRateTab
                  key={selectedRequestTab}
                  setHeaderActions={setHeaderActions}
                  statusFilter={requestStatusFilter}
                  onStatusFilterChange={setRequestStatusFilter}
                  source={
                    selectedRequestTab === 'Staff Request' ? 'admin' : 'web'
                  }
                  tabTitle={
                    selectedRequestTab === 'Staff Request'
                      ? 'Admin Rate Requests'
                      : 'Web Rate Requests'
                  }
                />
              </div>
            )}
            {selectedTab === 'List' && (
              <ListTab setHeaderActions={setHeaderActions} />
            )}
          </>
        ) : (
          <div className="delivered-rate-permission-denied-container">
            <div className="delivered-rate-permission-card">
              <div className="delivered-rate-permission-icon-wrap">
                <FiLock size={28} />
              </div>
              <h4 className="delivered-rate-permission-title">
                You do not have any permission
              </h4>
              <p className="delivered-rate-permission-desc">
                You do not have permission to view this section.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveredRate;
